import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, Stack, Grid, Chip, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { OptionsHeaderBar } from '../../components/options/OptionsHeaderBar';
import { useOptionsStore } from '../../store/useOptionsStore';
import { optionsService } from '../../services/optionsService';
import StorageIcon from '@mui/icons-material/Storage';

const DataHealthPage: React.FC = () => {
  const { healthReport, spotData, fetchHealthReport, fetchSpotData } = useOptionsStore();
  const [coverage, setCoverage] = useState<{ underlying: string; totalFiles: number; years: Array<{ year: string; fileCount: number; status: string }> } | null>(null);
  const [coverageError, setCoverageError] = useState<string | null>(null);

  useEffect(() => {
    fetchHealthReport();
    fetchSpotData();
    const interval = setInterval(() => {
      fetchHealthReport();
      fetchSpotData();
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    optionsService
      .getDatasetCoverage('NIFTY')
      .then((data) => {
        setCoverage(data);
        setCoverageError(null);
      })
      .catch((err) => setCoverageError(err?.message || 'Failed to load dataset coverage'));
  }, []);

  const statusEmoji = (status?: string) => {
    if (status === 'HEALTHY') return '🟢';
    if (status === 'WARNING') return '🟡';
    if (status === 'CRITICAL' || status === 'LOCKED') return '🔴';
    return '⚪';
  };

  const feedComponents = (healthReport?.components || []).filter((c) => c.category === 'FEED' || c.id === 'kite_ws');

  return (
    <Box sx={{ p: 3, bgcolor: '#F8FAFC', minHeight: '100vh' }}>
      <OptionsHeaderBar />

      <Grid container spacing={2.5}>
        {/* Live Feed Status */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF', height: '100%' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <StorageIcon sx={{ color: '#0284C7' }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B' }}>
                LIVE DATA FEED HEALTH
              </Typography>
            </Stack>

            {!healthReport && (
              <Typography variant="body2" sx={{ color: '#64748B' }}>
                Loading live health report…
              </Typography>
            )}

            <Stack spacing={1.5}>
              {feedComponents.map((c) => (
                <Paper key={c.id} elevation={0} sx={{ p: 1.5, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 1.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#0F172A' }}>
                        {c.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748B' }}>
                        {c.details}
                      </Typography>
                    </Box>
                    <Typography variant="body2">{statusEmoji(c.status)}</Typography>
                  </Stack>
                </Paper>
              ))}
              {spotData && (
                <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 1.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#0F172A' }}>
                        NIFTY 50 Spot Tick
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748B' }}>
                        ₹{spotData.spot.toFixed(1)} · last updated {new Date(spotData.lastUpdated).toLocaleTimeString('en-IN')}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              )}
            </Stack>
          </Paper>
        </Grid>

        {/* Historical Coverage Matrix */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B' }}>
                HISTORICAL DATASET COVERAGE (NIFTY)
              </Typography>
              {coverage && (
                <Chip size="small" label={`${coverage.totalFiles} FILES ON DISK`} sx={{ bgcolor: 'rgba(2, 132, 199, 0.12)', color: '#0284C7', fontWeight: 700 }} />
              )}
            </Stack>

            {coverageError && (
              <Typography variant="body2" sx={{ color: '#DC2626' }}>
                {coverageError}
              </Typography>
            )}
            {!coverageError && !coverage && (
              <Typography variant="body2" sx={{ color: '#64748B' }}>
                Scanning historical dataset directory…
              </Typography>
            )}

            {coverage && (
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#F8FAFC', '& th': { fontWeight: 600, color: '#64748B', fontSize: '0.72rem' } }}>
                    <TableCell>YEAR</TableCell>
                    <TableCell align="right">PARQUET FILES</TableCell>
                    <TableCell align="right">STATUS</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {coverage.years.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 3, color: '#64748B' }}>
                        No historical dataset files found on disk
                      </TableCell>
                    </TableRow>
                  )}
                  {coverage.years.map((y) => (
                    <TableRow key={y.year} hover sx={{ '& td': { fontSize: '0.78rem' } }}>
                      <TableCell sx={{ fontWeight: 700, color: '#0284C7' }}>{y.year}</TableCell>
                      <TableCell align="right">{y.fileCount}</TableCell>
                      <TableCell align="right">{y.status === 'INGESTING' ? '🟡 INGESTING' : '🟢 COMPLETE'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DataHealthPage;
